'use client';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import { Flex } from '@osmosis/tailwind-ui/components/Box';
import { borderColorBlue600 } from '@osmosis/tailwind-ui/styles/border';
import { bg0 } from '@osmosis/tailwind-ui/styles/background';
import { textPrimary } from '@osmosis/tailwind-ui/styles/text';
import { FormattedMessage, useIntl } from 'react-intl';

type SkipTarget = {
  linkText: string;
  /** data-testids in priority order; the first visible one is used. */
  linkUrl: string[];
};

type SkipLinkProps = {
  /** Single skip target (kept for backward compatibility). */
  linkText?: string;
  linkUrl?: string[];
  /** Multiple targets are rendered separately; unavailable targets are omitted. */
  links?: SkipTarget[];
  modalId?: string;
  style?: React.CSSProperties;
};

const findElementByTestId = (
  element: HTMLElement,
  testId: string,
): HTMLElement | null => {
  if (element.getAttribute('data-testid') === testId) {
    return element;
  }
  for (let i = 0; i < element.children.length; i++) {
    const foundElement = findElementByTestId(
      element.children[i] as HTMLElement,
      testId,
    );
    if (foundElement) {
      return foundElement;
    }
  }
  return null;
};

const findVisibleElementByTestId = (testId: string): HTMLElement | null => {
  const element = findElementByTestId(document.body, testId);
  if (!element || element.hidden) {
    return null;
  }
  const { display, visibility } = window.getComputedStyle(element);
  return display !== 'none' && visibility !== 'hidden' ? element : null;
};

const SkipLink = (props: SkipLinkProps) => {
  const { linkText, linkUrl, links, modalId = '', style } = props;
  const intl = useIntl();
  const [isVisible, setIsVisible] = useState(false);
  const [resolvedIds, setResolvedIds] = useState<(string | null)[]>([]);
  const firstTabPressedRef = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const firstItemRef = useRef<HTMLButtonElement>(null);
  const modalObserverRef = useRef<MutationObserver | null>(null);

  const targets: SkipTarget[] =
    links && links.length > 0
      ? links
      : [{ linkText: linkText ?? '', linkUrl: linkUrl ?? [] }];
  const targetsKey = JSON.stringify(targets);
  const availableTargets = targets.flatMap((target, index) => {
    const resolvedId = resolvedIds[index];
    return resolvedId ? [{ linkText: target.linkText, resolvedId }] : [];
  });
  const isMulti = availableTargets.length > 1;

  const handleScroll = useCallback((target: Element) => {
    target.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
      inline: 'nearest',
    });
  }, []);

  const handleSkip = useCallback(
    (targetId: string) => {
      const targetElement = findVisibleElementByTestId(targetId);
      if (!targetElement) {
        return;
      }

      const originalTabIndex = targetElement.getAttribute('tabIndex');
      if (!targetElement.hasAttribute('tabIndex')) {
        targetElement.setAttribute('tabIndex', '-1');
      }
      targetElement.focus();
      handleScroll(targetElement);

      setTimeout(() => {
        if (originalTabIndex === null) {
          targetElement.removeAttribute('tabIndex');
        } else {
          targetElement.setAttribute('tabIndex', originalTabIndex);
        }
      }, 0);
    },
    [handleScroll],
  );

  useEffect(() => {
    const parsedTargets: SkipTarget[] = JSON.parse(targetsKey);
    let currentKey = '';
    const resolveTargets = () => {
      const next = parsedTargets.map(
        ({ linkUrl: urls }) =>
          urls.find((url) => findVisibleElementByTestId(url) !== null) ?? null,
      );
      const nextKey = JSON.stringify(next);
      if (nextKey !== currentKey) {
        currentKey = nextKey;
        setResolvedIds(next);
      }
    };

    resolveTargets();
    const observer = new MutationObserver(resolveTargets);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'hidden', 'style', 'aria-hidden'],
    });
    return () => observer.disconnect();
  }, [targetsKey]);

  useEffect(() => {
    if (!modalId) {
      return;
    }
    const checkForModal = () => {
      if (!document.querySelector(`[data-testid="${modalId}"]`)) {
        setIsVisible(false);
      }
    };

    checkForModal();
    modalObserverRef.current = new MutationObserver(checkForModal);
    modalObserverRef.current.observe(document.body, {
      childList: true,
      subtree: true,
    });
    return () => modalObserverRef.current?.disconnect();
  }, [modalId]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || !firstItemRef.current) {
        return;
      }

      const activeElement = document.activeElement as HTMLElement;
      const isFormElement =
        activeElement.tagName === 'INPUT' ||
        activeElement.tagName === 'TEXTAREA';
      if (firstTabPressedRef.current || event.shiftKey || isFormElement) {
        setIsVisible(false);
        return;
      }

      event.preventDefault();
      setIsVisible(true);
      firstItemRef.current.focus();
      firstTabPressedRef.current = true;
    };

    const handleFocus = (event: FocusEvent) => {
      if (!firstTabPressedRef.current) {
        return;
      }
      setIsVisible(
        !!containerRef.current &&
          (containerRef.current === event.target ||
            containerRef.current.contains(event.target as Node)),
      );
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('focusin', handleFocus);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('focusin', handleFocus);
    };
  }, []);

  if (availableTargets.length === 0) {
    return null;
  }

  return (
    <Flex
      ref={containerRef}
      aria-label={
        isMulti
          ? intl.formatMessage({
              id: 'sidenav.skip.links.label',
              defaultMessage: 'Skip links',
            })
          : undefined
      }
      className={`skip-link ml-2 absolute ${
        isMulti ? 'flex-col gap-2' : ''
      } ${bg0} p-2 border ${borderColorBlue600} rounded-md z-[99999] ${
        isVisible
          ? 'pointer-events-auto top-[-25px] left-[25px]'
          : 'pointer-events-none top-[-1000px] left-[-1000px]'
      }`}
      style={{ opacity: isVisible ? 1 : 0, ...style }}
      data-testid={isMulti ? 'skip-links' : 'skip-link'}
    >
      {availableTargets.map((target, index) => (
        <Button
          key={`${target.resolvedId}-${index}`}
          as="button"
          type="button"
          variant="transparent"
          color="transparent"
          ref={index === 0 ? firstItemRef : undefined}
          onClick={() => handleSkip(target.resolvedId)}
          data-testid={
            index === 0
              ? 'accessibility-skip-link'
              : `accessibility-skip-link-${index}`
          }
          cn={`${textPrimary} font-bold underline`}
        >
          <FormattedMessage
            id="sidenav.skip.link"
            defaultMessage="Skip to {linkText}"
            values={{ linkText: target.linkText }}
          />
        </Button>
      ))}
    </Flex>
  );
};

export default SkipLink;
